import { useCallback } from 'react';

import { getAaId } from '@trello/authentication';
import { useSharedStateSelector } from '@trello/shared-state';

import type { TraitName, TraitValue } from './traits';
import { useCachedUserTraits } from './useCachedUserTraits';
import { userTraitsSharedState } from './userTraitsSharedState';

export const useUserTrait: (traitName: TraitName) => {
  value: TraitValue | undefined;
  loading: boolean;
} = (traitName: TraitName) => {
  const aaId = getAaId();

  useCachedUserTraits();

  const traitValue = useSharedStateSelector(
    userTraitsSharedState,
    useCallback(
      (traits) => {
        if (!aaId) {
          return { value: undefined, loading: false };
        }

        // if its loading or undefined for the user, we need to indicate that we are waiting for data to load
        // and not giving a false positive that the user just doesn't have any traits
        if (traits[aaId]?.loading || !traits[aaId]) {
          return { value: undefined, loading: true };
        }

        if (traits[aaId]?.attributes.length === 0) {
          return { value: undefined, loading: false };
        }

        return {
          value: traits[aaId]?.attributes.find(
            (trait) => trait.name === traitName,
          )?.value,
          loading: false,
        };
      },
      [aaId, traitName],
    ),
  );

  return traitValue;
};
